import React, { useState, useRef } from 'react';
import JoditEditor from 'jodit-react';

const CandidateNoteEditor = ({
  row = { note: '' },
  index = 0,
  tableData = [{ note: '' }],
  setTableData = () => { },
  setChanged = () => { }
}) => {
  const editorInstanceRef = useRef(null);
  const recognitionRef = useRef(null);
  const [activeLang, setActiveLang] = useState(null);
  const manualStopRef = useRef(false);

  // Replace text shortcuts
  const replaceShortcuts = (content) => {
    if (!content) return content;

    return content
      .replace(/\/1/g, '<strong>שאלה: </strong>')
      .replace(/\/2/g, '<strong>תשובה: </strong>')
      .replace(/\/5/g, '<strong>سؤال: </strong>')
      .replace(/\/6/g, '<strong>إجابة: </strong>');
  };

  const toggleSpeechRecognition = (langCode) => {
    if (activeLang === langCode && recognitionRef.current) {
      manualStopRef.current = true;
      recognitionRef.current.stop();
      return;
    }

    if (recognitionRef.current) {
      manualStopRef.current = true;
      recognitionRef.current.stop();
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert(
        'הדפדפן שלך אינו תומך בזיהוי קולי / متصفحك لا يدعم التعرف على الصوت'
      );
      return;
    }

    manualStopRef.current = false;

    const recognition = new SpeechRecognition();

    recognition.lang = langCode;
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    let baseContent = '';
    let accumulatedSpeech = '';

    recognition.onstart = () => {
      setActiveLang(langCode);
      accumulatedSpeech = '';

      if (editorInstanceRef.current) {
        baseContent = editorInstanceRef.current.value || '';

        if (
          baseContent &&
          !baseContent.endsWith(' ') &&
          !baseContent.endsWith('>')
        ) {
          baseContent += ' ';
        }
      } else {
        baseContent = row.note || '';
      }
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';

      for (
        let i = event.resultIndex;
        i < event.results.length;
        ++i
      ) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          accumulatedSpeech += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      const liveContent =
        baseContent + accumulatedSpeech + interimTranscript;

      if (editorInstanceRef.current) {
        editorInstanceRef.current.value = replaceShortcuts(liveContent);
      }
    };

    recognition.onend = () => {
      if (editorInstanceRef.current) {
        const finalContent = replaceShortcuts(
          editorInstanceRef.current.value
        );

        const updatedTableData = [...tableData];

        if (updatedTableData[index]) {
          updatedTableData[index].note = finalContent;
          setTableData(updatedTableData);
          setChanged(true);
        }
      }

      if (
        !manualStopRef.current &&
        recognitionRef.current === recognition
      ) {
        try {
          recognition.start();
          return;
        } catch (e) {
          // Ignore
        }
      }

      setActiveLang(null);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (e) {
      console.error(e);
    }
  };

  const config = {
    readonly: false,
    height: 400,
    width: 600,
    spellcheck: true,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    placeholder: 'הקלד דברי המועמד / اكتب أقوال المرشح',
    askBeforePasteFromWord: false,
    askBeforePasteHTML: false,
    direction: 'rtl',

    buttons: [
      'source',
      '|',
      'bold',
      'italic',
      '|',
      'ul',
      'ol',
      '|',
      'left',
      'center',
      'right',
      'justify',
      'spellcheck'
    ]
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        direction: 'rtl',
        width: 'fit-content'
      }}
    >
      {/* Voice Recorder Toggle Buttons */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          alignSelf: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => toggleSpeechRecognition('he-IL')}
          disabled={activeLang !== null && activeLang !== 'he-IL'}
          style={{
            padding: '5px 10px',          // Reduced padding
            fontSize: '12px',             // Added smaller font size
            backgroundColor:
              activeLang === 'he-IL' ? '#dc3545' : '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: activeLang !== null && activeLang !== 'he-IL' ? 'not-allowed' : 'pointer',
            opacity: activeLang !== null && activeLang !== 'he-IL' ? 0.6 : 1,
            fontWeight: 'bold'
          }}
        >
          {activeLang === 'he-IL'
            ? 'עצור הקלטה (עברית) ⏹️'
            : '🎤 התחל הקלטה (עברית)'}
        </button>

        <button
          type="button"
          onClick={() => toggleSpeechRecognition('ar-SA')}
          disabled={activeLang !== null && activeLang !== 'ar-SA'}
          style={{
            padding: '5px 10px',          // Reduced padding
            fontSize: '12px',             // Added smaller font size
            backgroundColor:
              activeLang === 'ar-SA' ? '#dc3545' : '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: activeLang !== null && activeLang !== 'ar-SA' ? 'not-allowed' : 'pointer',
            opacity: activeLang !== null && activeLang !== 'ar-SA' ? 0.6 : 1,
            fontWeight: 'bold'
          }}
        >
          {activeLang === 'ar-SA'
            ? 'إيقاف التسجيل (العربية) ⏹️'
            : '🎤 ابدأ التسجيل (العربية)'}
        </button>
      </div>

      {/* Jodit Rich Text Editor */}
      <div>
        <JoditEditor
          ref={editorInstanceRef}
          tabIndex={index}
          name="note"
          value={row.note}
          config={config}
          onBlur={(newContent) => {
            const updatedContent = replaceShortcuts(newContent);
            const updatedTableData = [...tableData];

            if (updatedTableData[index] && updatedTableData[index].note !== updatedContent) {
              updatedTableData[index].note = updatedContent;
              setTableData(updatedTableData);
            }
          }}
          onChange={(newContent) => {
            if (row.note !== newContent) {
              setChanged(true);
            }
          }}
        />
      </div>
    </div>
  );
};

export default CandidateNoteEditor;